import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const ScheduleVisitModal = ({ open, onOpenChange, project, visitForm, setVisitForm, handleScheduleVisit, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626]" aria-describedby="schedule-visit-description">
      <DialogHeader>
        <DialogTitle className="text-white">Schedule Site Visit</DialogTitle>
        <p id="schedule-visit-description" className="text-sm text-gray-500">Schedule a site visit for this project</p>
      </DialogHeader>
      <form onSubmit={handleScheduleVisit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-gray-400">Date *</Label><Input type="date" value={visitForm.scheduled_date} onChange={(e) => setVisitForm({...visitForm, scheduled_date: e.target.value})} className="bg-[#171717] border-[#262626]" required /></div>
          <div><Label className="text-gray-400">Time *</Label><Input type="time" value={visitForm.scheduled_time} onChange={(e) => setVisitForm({...visitForm, scheduled_time: e.target.value})} className="bg-[#171717] border-[#262626]" required /></div>
        </div>
        <div><Label className="text-gray-400">Purpose *</Label><Input value={visitForm.purpose} onChange={(e) => setVisitForm({...visitForm, purpose: e.target.value})} placeholder="e.g., Site inspection" className="bg-[#171717] border-[#262626]" required /></div>
        <div><Label className="text-gray-400">Notes</Label><Textarea value={visitForm.notes} onChange={(e) => setVisitForm({...visitForm, notes: e.target.value})} className="bg-[#171717] border-[#262626]" /></div>
        <div className="space-y-3 p-3 bg-[#171717] rounded border border-[#262626]">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-[#FF4500]" />
            <p className="text-sm font-medium text-white">Email Notifications</p>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Alert Client</p>
              <p className="text-xs text-gray-500">Send to {project?.client?.email || "client email"}</p>
            </div>
            <Switch
              checked={visitForm.alert_client}
              onCheckedChange={(checked) => setVisitForm({...visitForm, alert_client: checked, send_alerts: checked || visitForm.alert_team || visitForm.alert_founder})}
              disabled={!project?.client?.email}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Alert Associate Manager</p>
              <p className="text-xs text-gray-500">Send to manager</p>
            </div>
            <Switch
              checked={visitForm.alert_team}
              onCheckedChange={(checked) => setVisitForm({...visitForm, alert_team: checked, send_alerts: visitForm.alert_client || checked || visitForm.alert_founder})}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Alert Me (Founder)</p>
              <p className="text-xs text-gray-500">Send copy to myself</p>
            </div>
            <Switch
              checked={visitForm.alert_founder}
              onCheckedChange={(checked) => setVisitForm({...visitForm, alert_founder: checked, send_alerts: visitForm.alert_client || visitForm.alert_team || checked})}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-[#FF4500] hover:bg-[#E65100]">{submitting ? "Scheduling..." : "Schedule"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const ScheduleMeetingModal = ({ open, onOpenChange, project, meetingForm, setMeetingForm, handleScheduleMeeting, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626]" aria-describedby="schedule-meeting-description">
      <DialogHeader>
        <DialogTitle className="text-white">Schedule Meeting</DialogTitle>
        <p id="schedule-meeting-description" className="text-sm text-gray-500">Schedule online or site meeting</p>
      </DialogHeader>
      <form onSubmit={handleScheduleMeeting} className="space-y-4">
        <div><Label className="text-gray-400">Meeting Type</Label>
          <Select value={meetingForm.meeting_type} onValueChange={(value) => setMeetingForm({...meetingForm, meeting_type: value})}>
            <SelectTrigger className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-[#262626]">
              <SelectItem value="online">Online Meeting</SelectItem>
              <SelectItem value="site">Site Meeting</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-gray-400">Date *</Label><Input type="date" value={meetingForm.scheduled_date} onChange={(e) => setMeetingForm({...meetingForm, scheduled_date: e.target.value})} className="bg-[#171717] border-[#262626]" required /></div>
          <div><Label className="text-gray-400">Time *</Label><Input type="time" value={meetingForm.scheduled_time} onChange={(e) => setMeetingForm({...meetingForm, scheduled_time: e.target.value})} className="bg-[#171717] border-[#262626]" required /></div>
        </div>
        <div><Label className="text-gray-400">Agenda *</Label><Textarea value={meetingForm.agenda} onChange={(e) => setMeetingForm({...meetingForm, agenda: e.target.value})} placeholder="Meeting agenda..." className="bg-[#171717] border-[#262626]" required /></div>
        <div><Label className="text-gray-400">Attendees (comma separated)</Label><Input value={meetingForm.attendees} onChange={(e) => setMeetingForm({...meetingForm, attendees: e.target.value})} placeholder="Client, Architect, Manager" className="bg-[#171717] border-[#262626]" /></div>
        {meetingForm.meeting_type === 'site' && (
          <div><Label className="text-gray-400">Location</Label><Input value={meetingForm.location} onChange={(e) => setMeetingForm({...meetingForm, location: e.target.value})} placeholder="Site address" className="bg-[#171717] border-[#262626]" /></div>
        )}
        <div className="space-y-3 p-3 bg-[#171717] rounded border border-[#262626]">
          <div className="flex items-center gap-2 mb-2">
            <Bell className="w-4 h-4 text-[#FF4500]" />
            <p className="text-sm font-medium text-white">Email Notifications</p>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Alert Client</p>
              <p className="text-xs text-gray-500">Send to {project?.client?.email || "client email"}</p>
            </div>
            <Switch
              checked={meetingForm.alert_client}
              onCheckedChange={(checked) => setMeetingForm({...meetingForm, alert_client: checked, send_alerts: checked || meetingForm.alert_team || meetingForm.alert_founder})}
              disabled={!project?.client?.email}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Alert Associate Manager</p>
              <p className="text-xs text-gray-500">Send to manager</p>
            </div>
            <Switch
              checked={meetingForm.alert_team}
              onCheckedChange={(checked) => setMeetingForm({...meetingForm, alert_team: checked, send_alerts: meetingForm.alert_client || checked || meetingForm.alert_founder})}
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Alert Me (Founder)</p>
              <p className="text-xs text-gray-500">Send copy to myself</p>
            </div>
            <Switch
              checked={meetingForm.alert_founder}
              onCheckedChange={(checked) => setMeetingForm({...meetingForm, alert_founder: checked, send_alerts: meetingForm.alert_client || meetingForm.alert_team || checked})}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-[#262626]">Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-[#FF4500] hover:bg-[#E65100]">{submitting ? "Scheduling..." : "Schedule"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const RescheduleMeetingModal = ({ open, onOpenChange, editingMeeting, setEditingMeeting, meetingForm, setMeetingForm, handleUpdateMeeting, submitting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-[#1A1A1A] border-[#262626]" aria-describedby="edit-meeting-description">
      <DialogHeader>
        <DialogTitle className="text-white">Reschedule Meeting</DialogTitle>
        <p id="edit-meeting-description" className="text-sm text-gray-500">Change meeting date and time</p>
      </DialogHeader>
      <form onSubmit={handleUpdateMeeting} className="space-y-4">
        {editingMeeting && (
          <div className="p-3 bg-[#171717] rounded border border-[#262626]">
            <p className="text-sm text-white">{editingMeeting.agenda}</p>
            <p className="text-xs text-gray-500 mt-1">Current: {editingMeeting.scheduled_date} at {editingMeeting.scheduled_time}</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div><Label className="text-gray-400">New Date *</Label><Input type="date" value={meetingForm.scheduled_date} onChange={(e) => setMeetingForm({...meetingForm, scheduled_date: e.target.value})} className="bg-[#171717] border-[#262626]" required /></div>
          <div><Label className="text-gray-400">New Time *</Label><Input type="time" value={meetingForm.scheduled_time} onChange={(e) => setMeetingForm({...meetingForm, scheduled_time: e.target.value})} className="bg-[#171717] border-[#262626]" required /></div>
        </div>
        <div className="flex items-center gap-3 p-3 bg-[#171717] rounded border border-[#262626]">
          <Bell className="w-5 h-5 text-[#FF4500]" />
          <div className="flex-1">
            <p className="text-sm text-white">Alert Client & Team</p>
            <p className="text-xs text-gray-500">Send reschedule notification with updated calendar</p>
          </div>
          <Switch
            checked={meetingForm.send_alerts}
            onCheckedChange={(checked) => setMeetingForm({...meetingForm, send_alerts: checked})}
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => { onOpenChange(false); setEditingMeeting(null); }} className="border-[#262626]">Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-[#FF4500] hover:bg-[#E65100]">{submitting ? "Updating..." : "Reschedule"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);
